import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Plus } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@/contexts/PageContext';
import { usePartners, useCases, usePartnershipsAccess, canEditSection } from '@/hooks/usePartnerships';
import { CaseCard } from '@/components/partnerships/CaseCard';
import { CaseFormDialog } from '@/components/partnerships/CaseFormDialog';
import { PARTNER_CONFIG } from '@/components/partnerships/PartnerTheme';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import type { PartnerSlug, Case } from '../../../../shared/types/partnerships';

export function PartnerCasesPage() {
  const { t } = useTranslation();
  const { partner: slug } = useParams<{ partner: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [editCase, setEditCase] = useState<Case | undefined>();
  const [createCase, setCreateCase] = useState(false);

  const { data: access } = usePartnershipsAccess();
  const { data: partners = [], isPending: partnersPending } = usePartners();
  const { setPartnerCtx } = usePageContext();

  const partnerSlug = slug as PartnerSlug;
  const config = PARTNER_CONFIG[partnerSlug];

  const partner = partners.find(p => p.slug === partnerSlug);
  const partnerId = partner?.partnerId || '';
  const canEdit = canEditSection(access?.editScopes, 'cases');

  useEffect(() => {
    if (!partnerId || !config) return;
    setPartnerCtx({ partnerId, partnerName: config.name, partnerSlug });
    return () => setPartnerCtx(null);
  }, [partnerId, partnerSlug]);

  const { data: cases = [], isPending: listPending } = useCases(partnerId);
  // Mientras cargan los partners la query está deshabilitada y react-query v5 da isLoading=false:
  // sin esto se veía la lista vacía hasta recargar (feedback de Partnerships 07/10/2026).
  const isLoading = partnersPending || (!!partnerId && listPending);

  const filteredCases = useMemo(() =>
    search
      ? cases.filter(c =>
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          c.brand.toLowerCase().includes(search.toLowerCase())
        )
      : cases
  , [cases, search]);

  if (!config) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">{t('partnerships.common.partner_not_found')}</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate('/partnerships')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('common.ui.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={`/partnerships/${partnerSlug}`} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: config.color }} />
          <h1 className="text-2xl font-bold tracking-tight">{config.name} — {t('partnerships.cases.title')}</h1>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setCreateCase(true)}>
            <Plus className="h-3.5 w-3.5" /> {t('partnerships.cases.new_button')}
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('partnerships.cases.search_placeholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16"><WinclapLoader /></div>
      ) : filteredCases.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          {search ? t('partnerships.cases.no_results') : t('partnerships.cases.empty')}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCases.map(c => (
            <CaseCard
              key={c.caseId} caseItem={c} partnerSlug={partnerSlug}
              canEdit={canEdit} onEdit={setEditCase}
            />
          ))}
        </div>
      )}

      {partnerId && (
        <CaseFormDialog
          open={createCase || !!editCase}
          onOpenChange={o => { if (!o) { setCreateCase(false); setEditCase(undefined); } }}
          partnerId={partnerId}
          caseItem={editCase}
        />
      )}
    </div>
  );
}
